import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '../../auth/model/auth.store';
import type { ProjectEntity, TagEntity, TaskEntity } from '../../../entities/task/model/task.types';
import { PRIORITY_ORDER, PRIORITY_UI_LABEL } from '../../../shared/lib/priority';
import { Button } from '../../../shared/ui/Button';
import { showToast } from '../../../shared/ui/toast';
import { useTasksData, refreshTasks } from '../model/tasks.store';
import { useTaskUiStore } from '../model/tasks.ui';
import { checklistForTask, listProjectOptions, taskTagLinks } from '../model/selectors';
import {
  emptyTaskDraft,
  taskToDraft,
  tagIdsFromLinks,
  type TaskDraft,
} from '../model/taskDraft';
import { useTaskActions } from '../lib/useTaskActions';
import { describeTaskError } from '../lib/taskErrors';
import { formatDurationLabel, describeRecurrenceSummary } from '../lib/parseQuickAdd';
import { TagPicker } from './TagPicker';
import { ChecklistEditor } from './ChecklistEditor';
import { RecurrenceFields } from './RecurrenceFields';
import { ConfirmDialog } from './ConfirmDialog';
import { useUserDates } from '../lib/useUserDates';
import { DEFAULT_TIMEZONE } from '../../../shared/lib/constants';

/**
 * Панель редактирования: desktop — side panel, mobile — fullscreen.
 * Идея (is_idea): заголовок/описание + секция «Параметры задачи» (promote при сохранении).
 * Проект: select «Без проекта» + список; у идеи — только в параметрах (после promote).
 */
export function TaskEditorPanel() {
  const editorTaskId = useTaskUiStore((state) => state.editorTaskId);
  const closeEditor = useTaskUiStore((state) => state.closeTaskEditor);
  const data = useTasksData();
  const actions = useTaskActions();
  const { today } = useUserDates();
  const timezoneResolved = useAuthStore((state) => state.user?.timezone ?? DEFAULT_TIMEZONE);

  const task: TaskEntity | null = useMemo(() => {
    if (editorTaskId === null) {
      return null;
    }
    return data.tasks.find((item) => item.id === editorTaskId) ?? null;
  }, [data.tasks, editorTaskId]);

  const previousTagIds = useMemo(
    () => (task === null ? [] : tagIdsFromLinks(task.id, taskTagLinks(data, task.id))),
    [data, task],
  );
  const previousChecklist = useMemo(
    () => (task === null ? [] : checklistForTask(data, task.id)),
    [data, task],
  );

  const projects = useMemo(() => listProjectOptions(data), [data]);

  const [draft, setDraft] = useState<TaskDraft>(emptyTaskDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteIdeaConfirm, setDeleteIdeaConfirm] = useState(false);
  const [ideaParamsOpen, setIdeaParamsOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const titleRef = useRef<HTMLInputElement | null>(null);
  const bootedForTaskId = useRef<string | null>(null);

  useEffect(() => {
    if (task === null || bootedForTaskId.current === task.id) {
      return;
    }
    setDraft(taskToDraft(task, previousTagIds, previousChecklist));
    bootedForTaskId.current = task.id;
    setError(null);
    setIdeaParamsOpen(false);
    setPriorityOpen(false);
  }, [task, previousTagIds, previousChecklist]);

  useEffect(() => {
    if (editorTaskId !== null && task === null && !data.loading) {
      closeEditor();
    }
  }, [editorTaskId, task, data.loading, closeEditor]);

  useEffect(() => {
    if (editorTaskId === null) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeEditor();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => {
      titleRef.current?.focus();
    }, 50);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
    };
  }, [editorTaskId, closeEditor]);

  if (editorTaskId === null || task === null) {
    return null;
  }

  const isArchived = task.archived_at !== null;
  const isIdea = task.is_idea === true;
  const recurrenceLabel = draft.recurrenceEnabled
    ? describeRecurrenceSummary(
        draft.recurrence !== null
          ? JSON.stringify({
              frequency: draft.recurrence.frequency,
              interval: draft.recurrence.interval,
              ...(draft.recurrence.frequency === 'weekly'
                ? { weekdays: draft.recurrence.weekdays ?? [] }
                : {}),
              end_date: draft.recurrence.end_date ?? null,
            })
          : null,
      )
    : null;

  async function handleSave(): Promise<void> {
    if (task === null || saving) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const ok = await actions.saveTaskDraft(
        task.id,
        draft,
        {
          tagIds: previousTagIds,
          checklist: previousChecklist.map((item) => ({
            id: item.id,
            text: item.text,
            done: item.done,
            sortOrder: item.sort_order,
            isNew: false,
          })),
        },
        { isIdea: isIdea },
      );
      if (ok) {
        bootedForTaskId.current = null;
        closeEditor();
      }
    } catch (err) {
      setError(describeTaskError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleArchiveToggle(): Promise<void> {
    if (task === null) {
      return;
    }
    setSaving(true);
    try {
      if (isArchived) {
        const ok = await actions.restoreTask(task);
        if (ok) {
          bootedForTaskId.current = null;
          closeEditor();
        }
      } else {
        await actions.archiveTask(task.id);
        bootedForTaskId.current = null;
        closeEditor();
      }
    } catch (err) {
      setError(describeTaskError(err));
      showToast(describeTaskError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteIdea(): Promise<void> {
    if (task === null) {
      return;
    }
    setSaving(true);
    try {
      await actions.deleteIdea(task.id);
      bootedForTaskId.current = null;
      closeEditor();
    } catch (err) {
      setError(describeTaskError(err));
      showToast(describeTaskError(err), 'error');
    } finally {
      setSaving(false);
      setDeleteIdeaConfirm(false);
    }
  }

  const showTaskParams = !isIdea || ideaParamsOpen;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/55 md:hidden"
        aria-label="Закрыть редактор"
        onClick={closeEditor}
      />
      <aside
        className="fixed inset-x-0 bottom-0 top-0 z-50 flex flex-col border-tg-border-soft bg-tg-bg shadow-panel md:inset-y-0 md:left-auto md:right-0 md:w-[26rem] md:border-l"
        role="dialog"
        aria-modal="true"
        aria-label={isIdea ? 'Редактирование идеи' : 'Редактирование задачи'}
      >
        <header className="flex items-center justify-between gap-3 border-b border-tg-border-soft px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-tg-text">
              {isIdea
                ? 'Идея'
                : isArchived
                  ? 'Задача в архиве'
                  : 'Редактирование'}
            </h2>
            <p className="truncate text-xs text-tg-text-muted">
              {isIdea
                ? 'Заполните параметры задачи, чтобы оформить идею'
                : `${formatDurationLabel(draft.durationMinutes) ?? 'Без длительности'}${
                    recurrenceLabel !== null ? ` · ${recurrenceLabel}` : ''
                  }`}
            </p>
          </div>
          <button
            type="button"
            className="tg-btn-ghost h-9 min-h-9 px-3"
            onClick={closeEditor}
            aria-label="Закрыть редактор"
          >
            Закрыть
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {error !== null ? (
            <div className="rounded-xl border border-tg-danger/40 bg-tg-danger-soft px-3 py-2 text-sm text-tg-text">
              {error}
            </div>
          ) : null}

          <div>
            <label className="tg-label" htmlFor="task-title">
              {isIdea ? 'Идея' : 'Название'}
            </label>
            <input
              id="task-title"
              ref={titleRef}
              className="tg-input"
              value={draft.title}
              maxLength={500}
              onChange={(event) => {
                setDraft((prev) => ({ ...prev, title: event.target.value }));
              }}
            />
          </div>

          <div>
            <label className="tg-label" htmlFor="task-description">
              Описание
            </label>
            <textarea
              id="task-description"
              className="tg-input min-h-28 py-2"
              value={draft.description}
              maxLength={20_000}
              onChange={(event) => {
                setDraft((prev) => ({ ...prev, description: event.target.value }));
              }}
            />
          </div>

          {isIdea ? (
            <div className="rounded-xl border border-tg-border-soft bg-tg-surface p-3">
              <p className="text-sm text-tg-text-secondary">
                «Оформить как задачу» откроет приоритет, дату, проект, теги и другие параметры.
                При сохранении с параметрами идея станет задачей.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIdeaParamsOpen(true);
                  }}
                >
                  Оформить как задачу
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  disabled={saving}
                  onClick={() => {
                    setDeleteIdeaConfirm(true);
                  }}
                >
                  Удалить
                </Button>
              </div>
            </div>
          ) : null}

          {showTaskParams ? (
            <>
              {isIdea ? (
                <h3 className="text-sm font-semibold text-tg-text">Параметры задачи</h3>
              ) : null}

              <div className="relative">
                <p className="tg-label">Приоритет</p>
                <button
                  type="button"
                  className={`rounded-full border px-3 py-1.5 text-sm ${
                    priorityOpen
                      ? 'border-tg-accent/50 bg-tg-accent-soft text-tg-accent'
                      : 'border-tg-border-soft bg-tg-surface text-tg-text-secondary hover:text-tg-text'
                  }`}
                  aria-expanded={priorityOpen}
                  aria-haspopup="menu"
                  onClick={() => {
                    setPriorityOpen((value) => !value);
                  }}
                >
                  {PRIORITY_UI_LABEL[draft.priority]}
                  <span aria-hidden="true" className="ml-1 opacity-70">
                    ▼
                  </span>
                </button>
                {priorityOpen ? (
                  <div
                    className="absolute left-0 top-full z-30 mt-1 w-48 rounded-xl border border-tg-border-soft bg-tg-surface p-1 shadow-panel"
                    role="menu"
                    aria-label="Выбор приоритета"
                  >
                    {PRIORITY_ORDER.map((value) => (
                      <button
                        key={value}
                        type="button"
                        role="menuitemradio"
                        aria-checked={draft.priority === value}
                        className={`block w-full rounded-lg px-2.5 py-2 text-left text-sm ${
                          draft.priority === value
                            ? 'bg-tg-accent-soft text-tg-accent'
                            : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
                        }`}
                        onClick={() => {
                          setDraft((prev) => ({ ...prev, priority: value }));
                          setPriorityOpen(false);
                        }}
                      >
                        {PRIORITY_UI_LABEL[value]}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div>
                <label className="tg-label" htmlFor="task-project">
                  Проект
                </label>
                <select
                  id="task-project"
                  className="tg-input"
                  value={draft.projectId ?? ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    setDraft((prev) => ({
                      ...prev,
                      projectId: value.length === 0 ? null : value,
                    }));
                  }}
                >
                  <option value="">Без проекта</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="tg-label" htmlFor="task-date">
                    Дата
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="task-date"
                      type="date"
                      className="tg-input"
                      value={draft.dueDate ?? ''}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDraft((prev) => ({
                          ...prev,
                          dueDate: value.length === 0 ? null : value,
                        }));
                      }}
                    />
                    <button
                      type="button"
                      className="tg-btn-ghost shrink-0 px-2 text-xs"
                      onClick={() => {
                        setDraft((prev) => ({ ...prev, dueDate: null, dueTime: null }));
                      }}
                    >
                      Убрать
                    </button>
                  </div>
                  {draft.dueDate !== null ? (
                    <p className="mt-1 text-xs text-tg-text-muted">
                      {draft.dueDate === today ? 'Сегодня' : draft.dueDate}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-tg-text-muted">
                      Без даты — в списке «Без даты»
                    </p>
                  )}
                </div>

                <div>
                  <label className="tg-label" htmlFor="task-time">
                    Время
                  </label>
                  <input
                    id="task-time"
                    type="time"
                    className="tg-input"
                    value={draft.dueTime ?? ''}
                    disabled={draft.allDay || draft.dueDate === null}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraft((prev) => ({
                        ...prev,
                        dueTime: value.length === 0 ? null : value,
                      }));
                    }}
                  />
                  <label className="mt-2 flex items-center gap-2 text-sm text-tg-text-secondary">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-tg-accent"
                      checked={draft.allDay}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setDraft((prev) => ({
                          ...prev,
                          allDay: checked,
                          dueTime: checked ? null : prev.dueTime,
                        }));
                      }}
                    />
                    На весь день
                  </label>
                </div>
              </div>

              <div>
                <label className="tg-label" htmlFor="task-duration">
                  Длительность (мин)
                </label>
                <input
                  id="task-duration"
                  type="number"
                  min={0}
                  step={5}
                  className="tg-input"
                  value={draft.durationMinutes ?? ''}
                  placeholder="например 30"
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw.length === 0) {
                      setDraft((prev) => ({ ...prev, durationMinutes: null }));
                      return;
                    }
                    const value = Number(raw);
                    if (Number.isFinite(value) && value >= 0) {
                      setDraft((prev) => ({ ...prev, durationMinutes: Math.trunc(value) }));
                    }
                  }}
                />
              </div>

              <RecurrenceFields
                enabled={draft.recurrenceEnabled}
                rule={draft.recurrence}
                onToggle={(enabled) => {
                  setDraft((prev) => ({
                    ...prev,
                    recurrenceEnabled: enabled,
                    recurrence:
                      enabled && prev.recurrence === null
                        ? { frequency: 'daily', interval: 1, end_date: null }
                        : prev.recurrence,
                    recurrenceRule:
                      enabled && prev.recurrence !== null
                        ? prev.recurrenceRule
                        : enabled
                          ? prev.recurrenceRule
                          : null,
                  }));
                }}
                onRuleChange={(rule) => {
                  setDraft((prev) => ({
                    ...prev,
                    recurrence: rule,
                    recurrenceRule: prev.recurrenceRule,
                  }));
                }}
              />

              <TagPicker
                allTags={data.tags}
                selectedIds={draft.tagIds}
                onChange={(ids) => {
                  setDraft((prev) => ({ ...prev, tagIds: ids }));
                }}
                onCreateTag={(name, color) => actions.createTag(name, color)}
              />

              <ChecklistEditor
                items={draft.checklist}
                onChange={(items) => {
                  setDraft((prev) => ({ ...prev, checklist: items }));
                }}
              />

              {draft.dueDate !== null && draft.dueTime !== null && !draft.allDay ? (
                <p className="text-xs text-tg-text-muted">
                  Часовой пояс: {timezoneResolved} · {formatDurationLabel(draft.durationMinutes) ?? 'без длительности'}
                </p>
              ) : null}
            </>
          ) : null}
        </div>

        <footer className="space-y-2 border-t border-tg-border-soft px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              className="flex-1"
              loading={saving}
              onClick={() => void handleSave()}
            >
              {isIdea && ideaParamsOpen ? 'Оформить и сохранить' : 'Сохранить'}
            </Button>
            {!isIdea ? (
              <Button
                type="button"
                variant={isArchived ? 'secondary' : 'danger'}
                className="flex-1"
                disabled={saving}
                onClick={() => {
                  void handleArchiveToggle();
                }}
              >
                {isArchived ? 'Восстановить' : 'В архив'}
              </Button>
            ) : null}
          </div>
        </footer>
      </aside>

      <ConfirmDialog
        open={deleteIdeaConfirm}
        title="Удалить идею?"
        message={
          task === null
            ? ''
            : `Идея «${task.title}» будет удалена без возможности восстановления.`
        }
        confirmLabel="Удалить"
        danger
        onCancel={() => {
          setDeleteIdeaConfirm(false);
        }}
        onConfirm={() => {
          void handleDeleteIdea();
        }}
      />
    </>
  );
}

/** Открыть редактор и освежить данные (на случай внешних изменений). */
export function useOpenTaskEditor(): (taskId: string) => void {
  const openTaskEditor = useTaskUiStore((state) => state.openTaskEditor);
  return (taskId: string) => {
    void refreshTasks();
    openTaskEditor(taskId);
  };
}

export type { TagEntity, ProjectEntity };
